import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Award,
  Crown,
  FileBadge,
  Flame,
  Gift,
  Handshake,
  Instagram,
  Linkedin,
  Megaphone,
  MessageCircle,
  Rocket,
  ShieldCheck,
  Sparkles,
  Star,
  Trophy,
  Twitter,
  Users,
  Youtube,
  Zap,
} from "lucide-react";
import { api } from "@/lib/api";
import { useSeo } from "@/lib/useSeo";
import { SiteFooter, SiteHeader } from "@/components/SiteChrome";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const STEPS = [
  { n: "01", t: "Apply", d: "Fill the application with your details and social handles." },
  { n: "02", t: "Get reviewed", d: "We review applications within 7 days." },
  { n: "03", t: "Welcome kit", d: "Receive your unique referral code and dashboard access." },
  { n: "04", t: "Create + earn", d: "Submit content for review, climb the leaderboard." },
  { n: "05", t: "Win", d: "Top 3 at cohort end win exclusive rewards." },
];

const buildFaq = (cohort) => {
  const months = cohortDurationMonths(cohort);
  const durationCopy = months
    ? `${months} month${months === 1 ? "" : "s"} per cohort. Duration is set per cohort by the team — future cohorts may run shorter or longer based on the campaign.`
    : "Each cohort runs for a fixed window (set per cohort by the team — typically a few months). The exact dates for the active cohort are on this page.";
  return [
    { q: "Who can apply?", a: "Anyone can apply — students, working professionals, content creators, or influencers. If you're passionate about tech and have an audience, we want you." },
    { q: "How are ambassadors selected?", a: "Based on your social media presence, engagement, and motivation. We pick people who are actually shipping content." },
    { q: "How long is the program?", a: durationCopy },
    { q: "How do I earn points?", a: "By creating, sharing, reposting, screen-grabbing, or printing NoobSync content (Instagram posts, reels, LinkedIn write-ups, YouTube shorts, blog mentions, on-campus posters) — plus referring registrations to any NoobSync product. Anything that puts NoobSync in front of more people counts." },
    { q: "When are rewards announced?", a: "At the end of each cohort. Top 3 win exclusive rewards." },
    { q: "Can I be in future cohorts?", a: "Yes — once a cohort ends, you can apply again for the next one." },
  ];
};

const cohortDurationMonths = (cohort) => {
  if (!cohort?.start_date || !cohort?.end_date) return null;
  const s = new Date(cohort.start_date);
  const e = new Date(cohort.end_date);
  if (isNaN(s) || isNaN(e) || e <= s) return null;
  const days = Math.round((e - s) / 86400000);
  // Round to nearest month, with a minimum of 1
  return Math.max(1, Math.round(days / 30));
};

const PERKS = [
  { icon: FileBadge, title: "Official certificate", desc: "Recognized NoobSync Brand Ambassador certificate." },
  { icon: Crown, title: "Founding Ambassador badge", desc: "Permanent recognition. Founding members only.", highlight: true },
  { icon: Users, title: "Exclusive community", desc: "Private group of selected ambassadors from across the globe." },
  { icon: Rocket, title: "Early access", desc: "Try every NoobSync product before public launch." },
  { icon: MessageCircle, title: "Direct line to the founder", desc: "Talk to the people building NoobSync." },
  { icon: Trophy, title: "Top 3 win exclusive rewards", desc: "Announced at cohort end. Your input shapes them." },
  { icon: Star, title: "Top 10% get featured", desc: "Public recognition on our results page when the cohort ends." },
  { icon: Handshake, title: "Brand collaboration opportunities", desc: "Cross-promotion, co-marketing, and partnership opportunities for businesses and brands." },
];

function LeaderboardPreview({ data }) {
  const ranked = (data?.leaderboard || []).filter((r) => r.ranked);
  const top3 = ranked.slice(0, 3);
  const others = ranked.slice(3, 10);
  const colors = ["#FFD700", "#C0C0C0", "#CD7F32"];
  const showPoints = data?.show_points_public !== false;

  // Public-readiness gate: render a "warming up" state until the cohort hits
  // enough momentum. A leaderboard with 1 person at 0 pts looks dead and
  // undersells the program. Copy stays intentionally vague — no need to
  // expose the exact threshold publicly.
  if (data && data.is_public_ready === false) {
    return (
      <div data-testid="lb-warming-up" className="rounded-xl border border-dashed border-zinc-800 bg-zinc-950/60 p-12 text-center">
        <Trophy className="mx-auto h-10 w-10 text-zinc-700" />
        <p className="mt-3 font-display text-lg text-zinc-300">
          Leaderboard goes live soon
        </p>
        <p className="mt-2 text-sm text-zinc-500">
          Be one of the first to earn points and stake your claim.
        </p>
      </div>
    );
  }

  if (!ranked.length) {
    return (
      <div data-testid="lb-empty" className="rounded-xl border border-dashed border-zinc-800 bg-zinc-950/60 p-12 text-center">
        <Trophy className="mx-auto h-10 w-10 text-zinc-700" />
        <p className="mt-3 font-display text-lg text-zinc-300">
          Leaderboard goes live soon
        </p>
        <p className="mt-1 text-sm text-zinc-500">
          Apply now to be the first on the board.
        </p>
      </div>
    );
  }

  return (
    <div data-testid="lb-preview">
      <div className="grid grid-cols-3 gap-3 sm:gap-5">
        {top3.map((row, i) => (
          <div
            key={row.ambassador_id}
            data-testid={`lb-top-${i + 1}`}
            className="rounded-xl border bg-zinc-950/80 p-4 text-center"
            style={{ borderColor: `${colors[i]}66`, boxShadow: `0 0 30px ${colors[i]}26` }}
          >
            <div className="font-mono text-xs" style={{ color: colors[i] }}>
              #{row.rank}
            </div>
            <div className="mt-1 truncate font-display text-base font-bold text-white">
              {row.display_name}
            </div>
            <div className="truncate text-xs text-zinc-500">{row.display_subtitle || ""}</div>
            {showPoints && (
              <div className="mt-2 font-mono text-xl font-black" style={{ color: colors[i] }}>
                {row.points}
              </div>
            )}
          </div>
        ))}
      </div>
      {others.length > 0 && (
        <div className="mt-5 divide-y divide-zinc-900 rounded-xl border border-zinc-900 bg-zinc-950/60">
          {others.map((row) => (
            <div key={row.ambassador_id} className="flex items-center justify-between px-4 py-3">
              <div className="flex items-center gap-3">
                <span className="w-6 font-mono text-sm text-zinc-500">#{row.rank}</span>
                <div>
                  <p className="text-sm font-semibold text-white">{row.display_name}</p>
                  <p className="text-xs text-zinc-500">{row.display_subtitle || ""}</p>
                </div>
              </div>
              {showPoints && <div className="font-mono text-sm font-bold text-[#A855F7]">{row.points}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Landing() {
  useSeo({
    title: "NoobSync Brand Ambassador Program — Earn Points, Win Rewards",
    description: "Join the NoobSync Brand Ambassador Program. Represent NoobSync, post content, refer registrations, climb a live leaderboard, and win cash plus founder-track rewards. Apply free — no login required.",
    path: "/",
  });
  const [lb, setLb] = useState(null);
  const [cohort, setCohort] = useState(null);
  const [heroSocials, setHeroSocials] = useState([]);

  useEffect(() => {
    api.get("/leaderboard?limit=10").then((r) => setLb(r.data)).catch(() => {});
    api.get("/cohort/active").then((r) => setCohort(r.data.cohort)).catch(() => {});
    api.get("/site-settings").then((r) => {
      const socials = r.data?.socials || {};
      const ICONS = { instagram: Instagram, linkedin: Linkedin, twitter: Twitter, youtube: Youtube };
      const list = Object.entries(socials)
        .filter(([k, v]) => v?.show_in_hero && v?.url && ICONS[k])
        .map(([k, v]) => ({ key: k, url: v.url, Icon: ICONS[k] }));
      setHeroSocials(list);
    }).catch(() => {});
  }, []);

  const startDate = cohort?.start_date
    ? new Date(cohort.start_date).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })
    : null;
  const cohortName = cohort?.name;
  const cohortStatus = cohort?.status;
  const badgeText =
    cohortStatus === "active"
      ? (cohortName ? `${cohortName} — Live now` : "Live now")
      : startDate
        ? `Applications open — Starts ${startDate}`
        : "Applications open";

  return (
    <div className="min-h-screen bg-transparent text-white">
      <SiteHeader />

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-zinc-900">
        <div className="pointer-events-none absolute inset-0 grid-pattern opacity-40" />
        <div className="pointer-events-none absolute -top-32 left-1/3 h-[420px] w-[420px] radial-violet opacity-90" />
        <div className="relative mx-auto max-w-7xl px-5 py-20 md:py-28">
          <div className="grid items-center gap-12 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <div
                data-testid="cohort-badge"
                className="inline-flex items-center gap-2 rounded-full border border-[#A855F7]/40 bg-[#A855F7]/10 px-3 py-1 text-xs font-bold uppercase tracking-[0.18em] text-[#A855F7]"
              >
                <span className="h-2 w-2 animate-pulse rounded-full bg-[#A855F7]" />
                {badgeText}
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                {cohort?.theme_name && (
                  <span
                    data-testid="hero-theme-chip"
                    className="inline-flex items-center gap-1 rounded-full border border-cyan-500/30 bg-cyan-500/10 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.18em] text-cyan-300"
                  >
                    🎨 {cohort.theme_name}
                  </span>
                )}
                {cohort?.was_extended && (
                  <span
                    data-testid="hero-extended-chip"
                    className="inline-flex items-center gap-1 rounded-full border border-amber-400/30 bg-amber-500/10 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.18em] text-amber-300"
                    title="The cohort end date was pushed out by the team based on community feedback."
                  >
                    ⏱ Extended on community request
                  </span>
                )}
              </div>
              {(cohort?.sponsors || []).length > 0 && (
                <div
                  data-testid="hero-sponsors"
                  className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 rounded-xl border border-zinc-900 bg-zinc-950/60 px-4 py-2.5"
                >
                  <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-zinc-500">
                    Sponsored by
                  </span>
                  {(cohort.sponsors || []).map((s, i) => {
                    const inner = s.logo_url ? (
                      <img
                        src={s.logo_url}
                        alt={s.name}
                        className="h-6 max-w-[120px] object-contain"
                      />
                    ) : (
                      <span className="text-sm font-bold text-white">{s.name}</span>
                    );
                    const k = `${s.name || "sponsor"}-${i}`;
                    return s.website_url ? (
                      <a
                        key={k}
                        href={s.website_url}
                        target="_blank"
                        rel="noreferrer"
                        title={s.name}
                        data-testid={`hero-sponsor-${i}`}
                        className="inline-flex items-center transition hover:opacity-80"
                      >
                        {inner}
                      </a>
                    ) : (
                      <span key={k} data-testid={`hero-sponsor-${i}`} className="inline-flex items-center" title={s.name}>
                        {inner}
                      </span>
                    );
                  })}
                </div>
              )}
              <h1 className="mt-6 font-display text-5xl font-black leading-[0.95] tracking-tighter text-white sm:text-6xl lg:text-7xl">
                Become a <span className="brand-gradient-text">NoobSync</span>
                <br />
                Brand Ambassador.
              </h1>
              <p className="mt-6 max-w-xl text-base leading-relaxed text-zinc-400 sm:text-lg">
                Represent NoobSync. Earn points. Win rewards. Build your profile.
                Get founding-member access to every product we&apos;re shipping.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-3">
                <Link
                  to="/apply"
                  data-testid="hero-apply-cta"
                  className="group inline-flex items-center gap-2 rounded-md brand-gradient-bg px-6 py-3 font-bold text-white transition-all hover:-translate-y-0.5 hover:shadow-[0_10px_30px_-6px_rgba(168,85,247,0.6)]"
                >
                  Apply now
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Link>
                <Link
                  to="/leaderboard"
                  data-testid="hero-leaderboard-cta"
                  className="inline-flex items-center gap-2 rounded-md border border-zinc-800 px-6 py-3 font-semibold text-zinc-200 hover:border-zinc-600 hover:text-white"
                >
                  View leaderboard
                </Link>
              </div>
              <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-zinc-500">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4 text-[#A855F7]" /> {cohortDurationMonths(cohort) ? `${cohortDurationMonths(cohort)}-month cohort` : "Per-cohort program"}
                </div>
                <div className="flex items-center gap-2">
                  <Award className="h-4 w-4 text-[#A855F7]" />
                  {cohort?.prizes?.length
                    ? `${cohort.prize_currency || "₹"}${cohort.prizes.reduce((s, p) => s + (Number(p.amount) || 0), 0).toLocaleString("en-IN")} prize pool`
                    : "Top 3 rewards"}
                </div>
                <div className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-[#A855F7]" /> Founding badge
                </div>
              </div>
              {heroSocials.length > 0 && (
                <div className="mt-6 flex flex-wrap items-center gap-3" data-testid="hero-socials">
                  <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-zinc-600">Follow us</span>
                  {heroSocials.map(({ key, url, Icon }) => (
                    <a
                      key={key}
                      href={url}
                      target="_blank"
                      rel="noreferrer"
                      title={key}
                      data-testid={`hero-social-${key}`}
                      className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-zinc-800 text-zinc-400 transition hover:-translate-y-0.5 hover:border-[#A855F7]/60 hover:bg-[#A855F7]/10 hover:text-[#A855F7]"
                    >
                      <Icon className="h-4 w-4" />
                    </a>
                  ))}
                </div>
              )}
            </div>

            <div id="leaderboard" className="relative lg:col-span-5 scroll-mt-24">
              <div className="absolute inset-0 -z-10 rounded-3xl bg-gradient-to-br from-[#A855F7]/10 via-transparent to-[#22D3EE]/10 blur-2xl" />
              <div className="rounded-2xl border border-zinc-900 bg-zinc-950/80 p-6 backdrop-blur">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">
                    Live Leaderboard
                  </p>
                  <Flame className="h-4 w-4 text-[#22D3EE]" />
                </div>
                <div className="mt-4">
                  <LeaderboardPreview data={lb} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <PrizePoolSection prizes={cohort?.prizes} currency={cohort?.prize_currency} cohortName={cohortName} />

      {/* WHAT YOU DO */}
      <section id="what-you-do" className="border-b border-zinc-900">
        <div className="mx-auto max-w-7xl px-5 py-20">
          <div className="grid gap-12 md:grid-cols-2">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#A855F7]">
                What you do
              </p>
              <h2 className="mt-3 font-display text-3xl font-bold sm:text-4xl">
                Promote, post, refer, climb.
              </h2>
              <p className="mt-4 max-w-md text-zinc-400">
                NoobSync ambassadors are the first to know, the first to share,
                and the first to represent every product we build.
              </p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {[
                { icon: Instagram, t: "Post on Instagram & Reels", d: "Share NoobSync products with your network." },
                { icon: Linkedin, t: "Write on LinkedIn", d: "Build your personal brand while you do it." },
                { icon: Youtube, t: "Create videos", d: "YouTube videos and AI marketing videos earn the most points." },
                { icon: Megaphone, t: "Refer registrations", d: "Every successful referral = 100 points. Unlimited." },
              ].map(({ icon: Icon, t, d }) => (
                <div key={t} className="rounded-xl border border-zinc-900 bg-zinc-950/60 p-5 transition-all hover:-translate-y-1 hover:border-zinc-700">
                  <Icon className="h-5 w-5 text-[#A855F7]" />
                  <p className="mt-3 font-display font-semibold text-white">{t}</p>
                  <p className="mt-1 text-sm text-zinc-500">{d}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PERKS */}
      <section id="perks" className="border-b border-zinc-900">
        <div className="mx-auto max-w-7xl px-5 py-20">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#A855F7]">
            What you get
          </p>
          <h2 className="mt-3 max-w-2xl font-display text-3xl font-bold sm:text-4xl">
            Not just a badge. Real founding-member perks.
          </h2>
          <div className="mt-10 grid grid-cols-1 gap-4 md:grid-cols-2">
            {PERKS.map(({ icon: Icon, title, desc, highlight }, idx) => {
              const isLastOdd = idx === PERKS.length - 1 && PERKS.length % 2 === 1;
              return (
                <div
                  key={title}
                  className={`group rounded-2xl border bg-zinc-950/60 p-6 transition-all hover:-translate-y-1 ${isLastOdd ? "md:col-span-2" : ""} ${highlight ? "border-[#A855F7]/40 bg-gradient-to-br from-[#A855F7]/10 via-transparent to-transparent" : "border-zinc-900 hover:border-zinc-700"}`}
                >
                  <div className={`inline-flex h-10 w-10 items-center justify-center rounded-lg ${highlight ? "bg-[#A855F7] text-white" : "bg-zinc-900 text-[#A855F7]"}`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <p className={`mt-4 font-display text-lg font-bold ${highlight ? "text-[#A855F7]" : "text-white"}`}>
                    {title}
                  </p>
                  <p className="mt-1 text-sm text-zinc-400">{desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how-it-works" className="border-b border-zinc-900 scroll-mt-24">
        <div className="mx-auto max-w-7xl px-5 py-20">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#A855F7]">
            How it works
          </p>
          <h2 className="mt-3 font-display text-3xl font-bold sm:text-4xl">
            Five steps. One cohort.
          </h2>
          <div className="mt-10 grid gap-4 md:grid-cols-5">
            {STEPS.map((s) => (
              <div key={s.n} className="relative rounded-xl border border-zinc-900 bg-zinc-950/60 p-5">
                <span className="font-mono text-xs text-zinc-600">{s.n}</span>
                <p className="mt-2 font-display text-lg font-bold text-white">
                  {s.t}
                </p>
                <p className="mt-1 text-sm text-zinc-500">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LEADERBOARD FULL */}
      <section className="border-b border-zinc-900">
        <div className="mx-auto max-w-7xl px-5 py-20">
          <div className="flex items-end justify-between gap-6">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#A855F7]">
                Live leaderboard
              </p>
              <h2 className="mt-3 font-display text-3xl font-bold sm:text-4xl">
                See who&apos;s leading the cohort.
              </h2>
            </div>
            <Link to="/leaderboard" data-testid="full-leaderboard-link" className="hidden text-sm font-semibold text-[#A855F7] hover:underline md:block">
              See full leaderboard →
            </Link>
          </div>
          <div className="mt-10">
            <LeaderboardPreview data={lb} />
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="border-b border-zinc-900 scroll-mt-24">
        <div className="mx-auto max-w-3xl px-5 py-20">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#A855F7]">FAQ</p>
          <h2 className="mt-3 font-display text-3xl font-bold sm:text-4xl">
            Questions, answered.
          </h2>
          <Accordion type="single" collapsible className="mt-8" data-testid="faq-accordion">
            {buildFaq(cohort).map((f, i) => (
              <AccordionItem key={f.q} value={`item-${i}`} className="border-zinc-900">
                <AccordionTrigger data-testid={`faq-trigger-${i}`} className="text-left font-display font-semibold text-white hover:text-[#A855F7] hover:no-underline">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-zinc-400">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* FINAL CTA */}
      <section>
        <div className="mx-auto max-w-5xl px-5 py-20 text-center">
          <Gift className="mx-auto h-10 w-10 text-[#A855F7]" />
          <h2 className="mt-4 font-display text-4xl font-black tracking-tighter sm:text-5xl">
            Ready to represent NoobSync?
          </h2>
          <p className="mt-3 text-zinc-400">
            Spots are limited. Apply now and grab the Founding Ambassador
            badge — only available once.
          </p>
          <Link
            to="/apply"
            data-testid="final-apply-cta"
            className="mt-8 inline-flex items-center gap-2 rounded-md brand-gradient-bg px-7 py-3 font-bold text-white transition-all hover:-translate-y-0.5 hover:shadow-[0_10px_30px_-6px_rgba(168,85,247,0.6)]"
          >
            Apply now
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      {cohort?.collect_reward_ideas && <RewardSuggestSection />}

      <SiteFooter />
    </div>
  );
}

function PrizePoolSection({ prizes, currency = "₹", cohortName }) {
  if (!prizes?.length) return null;
  const fmt = (n) => (typeof n === "number" ? n.toLocaleString("en-IN") : n);
  const medals = ["🥇", "🥈", "🥉"];
  const accent = ["#FFD700", "#C0C0C0", "#CD7F32"];
  const total = prizes.reduce((s, p) => s + (Number(p.amount) || 0), 0);
  return (
    <section id="prizes" className="border-b border-zinc-900 scroll-mt-24" data-testid="prize-pool-section">
      <div className="mx-auto max-w-5xl px-5 py-20">
        <div className="text-center">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#A855F7]">
            {cohortName ? `${cohortName} · Prize pool` : "Prize pool"}
          </p>
          <h2 className="mt-3 font-display text-4xl font-black tracking-tighter sm:text-5xl">
            {currency}{fmt(total)} in cash prizes
          </h2>
          <p className="mt-3 text-zinc-400">Real money — not just clout. Finish in the Top 3 at cohort end and the cash is yours.</p>
        </div>
        <div className="mt-12 grid gap-5 sm:grid-cols-3">
          {prizes.slice(0, 3).map((p, i) => (
            <div
              key={p.rank || i}
              data-testid={`prize-card-${i + 1}`}
              className={`relative rounded-2xl border bg-zinc-950/80 p-8 text-center ${i === 0 ? "sm:-translate-y-3 sm:scale-105" : ""}`}
              style={{ borderColor: `${accent[i]}66`, boxShadow: `0 0 40px ${accent[i]}22` }}
            >
              <div className="text-4xl">{medals[i]}</div>
              <div className="mt-2 text-xs font-bold uppercase tracking-[0.2em]" style={{ color: accent[i] }}>
                {p.label || `Rank #${p.rank || i + 1}`}
              </div>
              <div className="mt-3 font-mono text-4xl font-black text-white">{currency}{fmt(p.amount)}</div>
            </div>
          ))}
        </div>
        <div className="mt-10 text-center">
          <Link
            to="/apply"
            data-testid="prize-apply-cta"
            className="inline-flex items-center gap-2 rounded-md brand-gradient-bg px-7 py-3 font-bold text-white transition-all hover:-translate-y-0.5 hover:shadow-[0_10px_30px_-6px_rgba(168,85,247,0.6)]"
          >
            Claim your shot — Apply now <ArrowRight className="h-4 w-4" />
          </Link>
          <p className="mt-3 text-xs text-zinc-600">Winners are the Top 3 on the leaderboard when the cohort ends.</p>
        </div>
      </div>
    </section>
  );
}

function RewardSuggestSection() {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [ideas, setIdeas] = useState(["", "", ""]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    const cleaned = ideas.map((s) => s.trim()).filter(Boolean);
    if (!email.trim() || !cleaned.length) {
      return;
    }
    setSubmitting(true);
    try {
      let ref = "";
      try {
        const fromUrl = new URLSearchParams(window.location.search).get("ref");
        ref = (fromUrl && /^NSA-/i.test(fromUrl)) ? fromUrl.toUpperCase() : (localStorage.getItem("ns_ref") || "");
      } catch {
        ref = "";
      }
      await api.post("/interest-signals", {
        email: email.trim(),
        name: name.trim() || null,
        reward_ideas: cleaned.slice(0, 3),
        referrer_code: ref || null,
      });
      setDone(true);
    } catch {
      // best-effort silent fail; user can retry
      setDone(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="suggest-reward" className="border-t border-zinc-900 bg-gradient-to-b from-zinc-950 to-black">
      <div className="mx-auto max-w-3xl px-5 py-16">
        <div className="rounded-2xl border border-[#A855F7]/25 bg-zinc-950 p-6 sm:p-10" data-testid="reward-suggest-card">
          <div className="flex items-start gap-3">
            <Sparkles className="h-7 w-7 shrink-0 text-[#A855F7]" />
            <div className="flex-1">
              <h3 className="font-display text-2xl font-black tracking-tight sm:text-3xl">
                Not the right moment to apply?
              </h3>
              <p className="mt-2 text-sm text-zinc-400">
                Drop your email + the rewards <span className="text-zinc-200">you&apos;d actually love</span>. We use this to plan the next cohort&apos;s reward pool — your input shapes what we offer.
              </p>
            </div>
          </div>

          {done ? (
            <div className="mt-6 rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-5 text-center" data-testid="reward-suggest-done">
              <p className="text-base font-bold text-emerald-300">Got it. Thank you.</p>
              <p className="mt-1 text-sm text-zinc-400">We&apos;ll factor your ideas into the next cohort and email you the moment applications reopen.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-6 space-y-3" data-testid="reward-suggest-form">
              <div className="grid gap-3 sm:grid-cols-2">
                <input
                  data-testid="reward-suggest-name"
                  type="text"
                  placeholder="Your name (optional)"
                  maxLength={120}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full rounded-md border border-zinc-800 bg-zinc-950 px-3 py-2.5 text-sm text-white focus:border-[#A855F7] focus:outline-none focus:ring-1 focus:ring-[#A855F7]"
                />
                <input
                  data-testid="reward-suggest-email"
                  type="email"
                  required
                  placeholder="you@example.com *"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full rounded-md border border-zinc-800 bg-zinc-950 px-3 py-2.5 text-sm text-white focus:border-[#A855F7] focus:outline-none focus:ring-1 focus:ring-[#A855F7]"
                />
              </div>
              {ideas.map((v, i) => (
                <input
                  key={i}
                  data-testid={`reward-suggest-idea-${i + 1}`}
                  type="text"
                  maxLength={80}
                  placeholder={i === 0 ? "Reward idea #1 *" : `Reward idea #${i + 1} (optional)`}
                  value={v}
                  onChange={(e) => setIdeas((prev) => prev.map((p, j) => (j === i ? e.target.value : p)))}
                  className="w-full rounded-md border border-zinc-800 bg-zinc-950 px-3 py-2.5 text-sm text-white focus:border-[#A855F7] focus:outline-none focus:ring-1 focus:ring-[#A855F7]"
                />
              ))}
              <button
                type="submit"
                disabled={submitting || !email.trim() || !ideas.some((s) => s.trim())}
                data-testid="reward-suggest-submit"
                className="inline-flex items-center gap-2 rounded-md brand-gradient-bg px-5 py-2.5 text-sm font-bold text-white transition-all hover:-translate-y-0.5 disabled:opacity-50"
              >
                {submitting ? "Sending…" : "Send my reward ideas"}
                <ArrowRight className="h-4 w-4" />
              </button>
              <p className="text-[11px] text-zinc-600">
                We&apos;ll only use your email to notify you about the next cohort. No spam.
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
